import { useEffect, useState, FormEvent, ChangeEvent, lazy, Suspense } from 'react'
import { Navigate, useNavigate } from 'react-router-dom'
import { tenantConfig } from '../config/tenant'
import { api, getToken, clearToken, ApiError } from '../api/client'
import Logo from '../components/Logo'
import Seo from '../components/Seo'

// Blog authoring is admin-only and pulls in TipTap — lazy-load it so the editor
// never ships in the public bundle (it only mounts inside auth-gated /dataentry).
const BlogSection = lazy(() => import('../components/BlogSection'))

interface Institution {
  id: string
  name: string
  code: string
  is_active: boolean
  is_major?: boolean
  tier?: number | null
  show_in_carousel?: boolean
}

interface Product {
  id: string
  institution_id: string
  product_name: string
  product_type: string
  interest_rate: number
  comparison_rate?: number | null
  rate_locked?: boolean
  is_active: boolean
}

// A rate row as read from a screenshot (rate is a PERCENTAGE, e.g. 5.88).
interface RateRow {
  lender_name: string
  product_name: string
  rate: number
  product_type?: string
  comparison_rate?: number | null
}

export default function DataEntryPage() {
  const token = getToken()
  const navigate = useNavigate()

  const [institutions, setInstitutions] = useState<Institution[]>([])
  const [products, setProducts] = useState<Product[]>([])
  const [instFilter, setInstFilter] = useState<string>('')
  const [statusFilter, setStatusFilter] = useState<'active' | 'inactive' | 'all'>('active')
  const [error, setError] = useState<string | null>(null)

  const [showInstModal, setShowInstModal] = useState(false)
  const [showProdModal, setShowProdModal] = useState(false)
  const [showPwModal, setShowPwModal] = useState(false)
  const [refreshing, setRefreshing] = useState(false)
  const [refreshMsg, setRefreshMsg] = useState<string | null>(null)
  const [autoRefresh, setAutoRefresh] = useState<{ enabled: boolean; as_at: string | null } | null>(null)
  const [displayCount, setDisplayCount] = useState<number>(5)
  const [shotRows, setShotRows] = useState<RateRow[] | null>(null) // review modal open when non-null
  const [shotBusy, setShotBusy] = useState(false)
  // Leads & billing (Phase 0): per-lead rate + date-ranged CSV export.
  const [leadRate, setLeadRate] = useState<number>(30)
  const [exportFrom, setExportFrom] = useState<string>(() => {
    const d = new Date()
    return new Date(d.getFullYear(), d.getMonth(), 1).toISOString().slice(0, 10)
  })
  const [exportTo, setExportTo] = useState<string>(() => new Date().toISOString().slice(0, 10))
  const [exporting, setExporting] = useState(false)
  const [exportMsg, setExportMsg] = useState<string | null>(null)
  const [billingSummary, setBillingSummary] = useState<{ events: number; billable: number; total_amount_aud: number } | null>(null)
  // Leads & billing is a LoanLoop-operator surface, not a tenant-admin one — a
  // client admin (e.g. Chelston) must not see it. Server is the real gate (403);
  // this just hides the UI. Defaults hidden until /me confirms operator.
  const [isOperator, setIsOperator] = useState<boolean>(false)
  const [role, setRole] = useState<string | null>(null)
  const [activeTab, setActiveTab] = useState<'rates' | 'blog' | 'leads'>('rates')

  useEffect(() => {
    if (!token) return
    api.get<Institution[]>('/api/admin/institutions')
      .then(setInstitutions)
      .catch(handleAuthError)
  }, [])

  useEffect(() => {
    if (!token) return
    const params = new URLSearchParams()
    if (instFilter) params.set('institution_id', instFilter)
    if (statusFilter === 'active') params.set('is_active', 'true')
    if (statusFilter === 'inactive') params.set('is_active', 'false')
    const qs = params.toString()
    api.get<Product[]>(`/api/admin/loans${qs ? `?${qs}` : ''}`)
      .then(setProducts)
      .catch(handleAuthError)
  }, [instFilter, statusFilter])

  useEffect(() => {
    if (!token) return
    api.get<{ enabled: boolean; as_at: string | null }>('/api/admin/rates/auto-refresh')
      .then(setAutoRefresh)
      .catch(() => { /* non-fatal: toggle just won't render */ })
    api.get<{ count: number }>('/api/admin/rates/display-count')
      .then((r) => setDisplayCount(r.count))
      .catch(() => { /* non-fatal: defaults to 5 */ })
    api.get<{ is_operator: boolean; role?: string }>('/api/auth/me')
      .then((r) => {
        const op = !!r?.is_operator
        setIsOperator(op)
        setRole(r?.role ?? null)
        if (op) {
          api.get<{ rate: number }>('/api/admin/leads/lead-rate')
            .then((rr) => { if (typeof rr?.rate === 'number') setLeadRate(rr.rate) })
            .catch(() => { /* non-fatal: defaults to 30 */ })
        }
      })
      .catch(() => { /* non-fatal: section stays hidden */ })
  }, [])

  // Billing summary from the ledger for the chosen range (operator only).
  useEffect(() => {
    if (!token || !isOperator) return
    const qs = new URLSearchParams()
    if (exportFrom) qs.set('from', exportFrom)
    if (exportTo) qs.set('to', exportTo)
    api.get<{ events: number; billable: number; total_amount_aud: number }>(`/api/admin/leads/billing-summary?${qs.toString()}`)
      .then(setBillingSummary)
      .catch(() => setBillingSummary(null))
  }, [isOperator, exportFrom, exportTo])

  // Auth gate — placed after all hooks so hook order stays stable (rules-of-hooks).
  if (!token) {
    if (typeof window !== 'undefined') {
      window.history.replaceState({}, '', '/dataentry/login')
    }
    return <Navigate to="/dataentry/login" replace />
  }

  function handleAuthError(err: unknown) {
    if (err instanceof ApiError && (err.status === 401 || err.status === 403)) {
      clearToken()
      navigate('/dataentry/login')
      return
    }
    setError(err instanceof Error ? err.message : 'Something went wrong.')
  }

  async function toggleProduct(productId: string) {
    try {
      const updated = await api.patch<Product>(`/api/admin/loans/${productId}/toggle-status`)
      setProducts((prev) => prev.map((p) => (p.id === productId ? updated : p)))
    } catch (err) {
      handleAuthError(err)
    }
  }

  async function saveDisplayCount(n: number) {
    const clamped = Math.max(1, Math.min(20, n || 5))
    setDisplayCount(clamped)
    try {
      await api.put('/api/admin/rates/display-count', { count: clamped })
    } catch (err) {
      handleAuthError(err)
    }
  }

  async function saveLeadRate(n: number) {
    const clamped = Math.max(0, Number.isFinite(n) ? n : 30)
    setLeadRate(clamped)
    try {
      await api.put('/api/admin/leads/lead-rate', { rate: clamped })
    } catch (err) {
      handleAuthError(err)
    }
  }

  async function exportLeads() {
    setExporting(true)
    setExportMsg(null)
    try {
      const qs = new URLSearchParams()
      if (exportFrom) qs.set('from', exportFrom)
      if (exportTo) qs.set('to', exportTo)
      const blob = await api.download(`/api/admin/leads/export?${qs.toString()}`)
      const href = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = href
      a.download = `leads-${exportFrom}_to_${exportTo}.csv`
      document.body.appendChild(a)
      a.click()
      a.remove()
      URL.revokeObjectURL(href)
      setExportMsg('Export downloaded.')
    } catch (err) {
      if (err instanceof ApiError) setExportMsg(err.message)
      else handleAuthError(err)
    } finally {
      setExporting(false)
    }
  }

  const productQs = () =>
    statusFilter === 'active' ? '?is_active=true' : statusFilter === 'inactive' ? '?is_active=false' : ''

  // "Refresh rates" — triggers the headless-scrape workflow (async, ~1 min).
  async function refreshRates() {
    setRefreshing(true)
    setRefreshMsg(null)
    try {
      await api.post('/api/admin/rates/refresh')
      setRefreshMsg('Refresh started. New rates appear in ~1 min. Reload the page shortly.')
    } catch (err) {
      if (err instanceof ApiError) setRefreshMsg(err.message)
      else handleAuthError(err)
    } finally {
      setRefreshing(false)
    }
  }

  async function toggleAutoRefresh() {
    if (!autoRefresh) return
    const next = !autoRefresh.enabled
    setAutoRefresh({ ...autoRefresh, enabled: next })
    try {
      await api.put('/api/admin/rates/auto-refresh', { enabled: next })
    } catch (err) {
      setAutoRefresh({ ...autoRefresh, enabled: !next }) // revert on failure
      handleAuthError(err)
    }
  }

  // "From screenshot" — read rates off an uploaded LMG grab, then open the review modal.
  async function onScreenshotPick(e: ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0]
    e.target.value = '' // allow re-picking the same file
    if (!file) return
    setShotBusy(true)
    setRefreshMsg(null)
    try {
      const form = new FormData()
      form.append('file', file)
      const res = await api.upload<{ rows: RateRow[] }>('/api/admin/rates/extract-screenshot', form)
      setShotRows(res.rows)
    } catch (err) {
      if (err instanceof ApiError) setRefreshMsg(err.message)
      else handleAuthError(err)
    } finally {
      setShotBusy(false)
    }
  }

  function editShotRow(i: number, patch: Partial<RateRow>) {
    setShotRows((prev) => (prev ? prev.map((r, idx) => (idx === i ? { ...r, ...patch } : r)) : prev))
  }

  async function applyShotRows() {
    if (!shotRows || shotRows.length === 0) return
    setShotBusy(true)
    try {
      const res = await api.post<{ refreshed: number; as_at: string }>('/api/admin/rates/apply', {
        rows: shotRows,
        source: 'screenshot',
      })
      setRefreshMsg(`Applied ${res.refreshed} rates from screenshot · as at ${res.as_at}`)
      setShotRows(null)
      setProducts(await api.get<Product[]>(`/api/admin/loans${productQs()}`))
    } catch (err) {
      handleAuthError(err)
    } finally {
      setShotBusy(false)
    }
  }

  async function toggleInstitution(instId: string) {
    try {
      const updated = await api.patch<Institution>(`/api/admin/institutions/${instId}/toggle-status`)
      setInstitutions((prev) => prev.map((i) => (i.id === instId ? updated : i)))
    } catch (err) {
      handleAuthError(err)
    }
  }

  async function saveProductEdit(
    id: string,
    patch: Partial<Product> & { interest_rate_percent?: number; comparison_rate_percent?: number | null },
  ) {
    // Frontend passes rate as a percent (e.g. 5.79) to match how brokers think;
    // backend expects the same and converts to decimal (0.0579) via /100 in admin.py.
    const body: Record<string, unknown> = {}
    if (patch.product_name !== undefined) body.product_name = patch.product_name
    if (patch.product_type !== undefined) body.product_type = patch.product_type
    if (patch.institution_id !== undefined) body.institution_id = patch.institution_id
    if (patch.interest_rate_percent !== undefined) body.interest_rate = patch.interest_rate_percent
    if (patch.comparison_rate_percent !== undefined) body.comparison_rate = patch.comparison_rate_percent
    if (patch.rate_locked !== undefined) body.rate_locked = patch.rate_locked
    try {
      const updated = await api.patch<Product>(`/api/admin/loans/${id}`, body)
      setProducts((prev) => prev.map((p) => (p.id === id ? updated : p)))
      return true
    } catch (err) {
      handleAuthError(err)
      return false
    }
  }

  async function saveInstitutionEdit(id: string, patch: Partial<Institution>) {
    try {
      const updated = await api.patch<Institution>(`/api/admin/institutions/${id}`, patch)
      setInstitutions((prev) => prev.map((i) => (i.id === id ? updated : i)))
      return true
    } catch (err) {
      handleAuthError(err)
      return false
    }
  }

  function institutionName(id: string) {
    return institutions.find((i) => i.id === id)?.name ?? 'Unknown'
  }

  return (
    <main className="min-h-screen bg-[#FAFAF7]">
      <Seo title="Broker Portal" path="/dataentry" noindex />
      {/* Top bar */}
      <header className="border-b border-[#E8E8E2] bg-white">
        <div className="max-w-7xl mx-auto px-6 py-2 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Logo size={28} />
            <div className="border-l border-[#E8E8E2] pl-3">
              <div className="text-xs text-gs-text-muted">Broker portal · Data entry</div>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <button
              onClick={() => setShowPwModal(true)}
              className="text-sm text-gs-text-soft hover:text-ink"
            >
              Change password
            </button>
            <button
              onClick={() => { clearToken(); navigate('/dataentry/login') }}
              className="text-sm text-gs-text-soft hover:text-ink"
            >
              Sign out
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-6 py-10 space-y-8">
        {/* Tabs */}
        <nav className="flex gap-1 border-b border-[#E8E8E2]" aria-label="Sections">
          {([
            { id: 'rates', label: 'Rates & products', show: true },
            { id: 'blog', label: 'Blog', show: role === 'admin' },
            { id: 'leads', label: 'Leads & billing', show: isOperator },
          ] as const).filter((t) => t.show).map((t) => (
            <button
              key={t.id}
              type="button"
              role="tab"
              aria-selected={activeTab === t.id}
              onClick={() => setActiveTab(t.id)}
              className={`px-4 py-2 text-sm font-semibold -mb-px border-b-2 ${
                activeTab === t.id ? 'border-ink text-ink' : 'border-transparent text-gs-text-muted hover:text-ink'
              }`}
            >
              {t.label}
            </button>
          ))}
        </nav>

        {activeTab === 'rates' && (
        <div className="grid gap-8 lg:grid-cols-[280px_1fr]">
        {/* Institutions column */}
        <section aria-labelledby="institutions-heading" className="space-y-4">
          <div className="flex items-center justify-between">
            <h2 id="institutions-heading" className="font-heading text-lg text-ink">Institutions</h2>
            <button
              onClick={() => setShowInstModal(true)}
              className="text-xs font-medium px-3 py-1.5 rounded-lg border border-[#D8D8D0] hover:bg-white"
            >
              New institution
            </button>
          </div>
          <ul className="border border-[#E8E8E2] rounded-2xl bg-white divide-y divide-[#E8E8E2]">
            {institutions.map((i) => (
              <InstitutionRow
                key={i.id}
                institution={i}
                onSave={saveInstitutionEdit}
                onToggle={toggleInstitution}
              />
            ))}
            {institutions.length === 0 && (
              <li className="px-4 py-6 text-sm text-gs-text-muted text-center">No institutions yet.</li>
            )}
          </ul>
        </section>

        {/* Products column */}
        <section aria-labelledby="products-heading" className="space-y-4">
          <div className="flex items-center justify-between">
            <h2 id="products-heading" className="font-heading text-lg text-ink">Products & rates</h2>
            <div className="flex items-center gap-2">
              <button
                onClick={refreshRates}
                disabled={refreshing || shotBusy}
                className="text-sm font-medium px-4 py-2 rounded-lg border border-[#D8D8D0] hover:bg-white transition-colors disabled:opacity-50 inline-flex items-center gap-2"
                title="Scrape the latest live rates from Loan Market (runs in the background, ~1 min)"
              >
                <span aria-hidden="true" className={refreshing ? 'inline-block animate-spin' : 'inline-block'}>↻</span>
                {refreshing ? 'Starting…' : 'Refresh rates'}
              </button>
              <label
                className={`text-sm font-medium px-4 py-2 rounded-lg border border-[#D8D8D0] hover:bg-white transition-colors inline-flex items-center gap-2 cursor-pointer ${shotBusy ? 'opacity-50 pointer-events-none' : ''}`}
                title="Read rates from an uploaded Loan Market screenshot"
              >
                <span aria-hidden="true" className={shotBusy ? 'inline-block animate-spin' : 'inline-block'}>{shotBusy ? '↻' : '⇪'}</span>
                {shotBusy ? 'Reading…' : 'From screenshot'}
                <input type="file" accept="image/*" className="sr-only" onChange={onScreenshotPick} disabled={shotBusy} />
              </label>
              <button
                onClick={() => setShowProdModal(true)}
                className="text-sm font-medium px-4 py-2 rounded-lg"
                style={{ background: tenantConfig.accentColor, color: tenantConfig.primaryColor }}
              >
                Add product
              </button>
            </div>
          </div>

          {autoRefresh && (
            <div className="flex items-center justify-between rounded-lg border border-[#E8E8E2] bg-[#FAFAF7] px-3 py-2 text-sm">
              <div>
                <span className="font-medium text-ink">Auto-refresh daily</span>
                <span className="text-gs-text-muted">
                  {' '}· ~9am AEST{autoRefresh.as_at ? ` · last refreshed ${autoRefresh.as_at}` : ''}
                </span>
              </div>
              <button
                type="button"
                role="switch"
                aria-checked={autoRefresh.enabled}
                aria-label="Toggle daily rate auto-refresh"
                onClick={toggleAutoRefresh}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${autoRefresh.enabled ? '' : 'bg-[#D8D8D0]'}`}
                style={autoRefresh.enabled ? { background: tenantConfig.primaryColor } : undefined}
              >
                <span
                  className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${autoRefresh.enabled ? 'translate-x-6' : 'translate-x-1'}`}
                />
              </button>
            </div>
          )}

          <div className="flex items-center justify-between rounded-lg border border-[#E8E8E2] bg-[#FAFAF7] px-3 py-2 text-sm">
            <label htmlFor="rate-display-count" className="font-medium text-ink">
              Rate rows shown before scrolling
              <span className="text-gs-text-muted font-normal"> · public rate boxes</span>
            </label>
            <input
              id="rate-display-count"
              type="number"
              min={1}
              max={20}
              value={displayCount}
              onChange={(e) => setDisplayCount(parseInt(e.target.value, 10) || 0)}
              onBlur={(e) => saveDisplayCount(parseInt(e.target.value, 10) || 5)}
              className="w-20 px-2 py-1 border border-[#D8D8D0] rounded text-sm tab-num text-right"
            />
          </div>

          {refreshMsg && (
            <div className="text-sm rounded-lg px-3 py-2 border border-[#D8E8B0] bg-lime-tint text-ink">
              {refreshMsg}
            </div>
          )}

          <div className="flex gap-4">
            <div className="flex-1">
              <label htmlFor="filter-inst" className="text-xs text-gs-text-muted block mb-1">Filter by institution</label>
              <select
                id="filter-inst"
                value={instFilter}
                onChange={(e) => setInstFilter(e.target.value)}
                className="w-full px-3 py-2 border border-[#D8D8D0] rounded-lg text-sm bg-white"
              >
                <option value="">All institutions</option>
                {institutions.map((i) => (
                  <option key={i.id} value={i.id}>{i.name}</option>
                ))}
              </select>
            </div>
            <div className="flex-1">
              <label htmlFor="filter-status" className="text-xs text-gs-text-muted block mb-1">Filter by status</label>
              <select
                id="filter-status"
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value as 'active' | 'inactive' | 'all')}
                className="w-full px-3 py-2 border border-[#D8D8D0] rounded-lg text-sm bg-white"
              >
                <option value="active">Active only</option>
                <option value="inactive">Inactive only</option>
                <option value="all">All</option>
              </select>
            </div>
          </div>

          {error && (
            <div className="text-red-600 text-sm border border-red-200 bg-red-50 rounded-lg px-3 py-2">
              {error}
            </div>
          )}

          <div className="border border-[#E8E8E2] rounded-2xl bg-white overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-[#F4F4F0] text-left text-xs text-gs-text-soft uppercase tracking-wide">
                <tr>
                  <th className="px-4 py-3">Product</th>
                  <th className="px-4 py-3">Institution</th>
                  <th className="px-4 py-3">Type</th>
                  <th className="px-4 py-3 text-right">Rate</th>
                  <th className="px-4 py-3 text-center">Status</th>
                  <th className="px-4 py-3 text-right w-24">Edit</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[#E8E8E2]">
                {products.map((p) => (
                  <ProductRow
                    key={p.id}
                    product={p}
                    institutions={institutions}
                    institutionName={institutionName}
                    onSave={saveProductEdit}
                    onToggle={toggleProduct}
                  />
                ))}
                {products.length === 0 && (
                  <tr>
                    <td colSpan={6} className="px-4 py-8 text-center text-gs-text-muted">
                      No products match these filters.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </section>
        </div>
        )}

        {activeTab === 'blog' && role === 'admin' && (
          <div className="max-w-4xl">
            <Suspense fallback={<div className="text-sm text-gs-text-muted">Loading blog editor…</div>}>
              <BlogSection />
            </Suspense>
          </div>
        )}

        {activeTab === 'leads' && isOperator && (
        <section aria-labelledby="leads-heading" className="space-y-4 max-w-4xl">
          <div className="flex items-center justify-between">
            <h2 id="leads-heading" className="font-heading text-lg text-ink">Leads &amp; billing</h2>
            <span className="text-xs text-gs-text-muted">LoanLoop operator only</span>
          </div>

          <div className="flex items-center justify-between rounded-lg border border-[#E8E8E2] bg-[#FAFAF7] px-3 py-2 text-sm">
            <label htmlFor="lead-rate" className="font-medium text-ink">
              Rate per billable lead
              <span className="text-gs-text-muted font-normal"> · AUD · repeat enquiries bill $0</span>
            </label>
            <div className="flex items-center gap-1">
              <span className="text-gs-text-muted">$</span>
              <input
                id="lead-rate"
                type="number"
                min={0}
                step="0.01"
                value={leadRate}
                onChange={(e) => setLeadRate(parseFloat(e.target.value) || 0)}
                onBlur={(e) => saveLeadRate(parseFloat(e.target.value) || 0)}
                className="w-24 px-2 py-1 border border-[#D8D8D0] rounded text-sm tab-num text-right"
              />
            </div>
          </div>

          <div className="rounded-lg border border-[#E8E8E2] bg-[#FAFAF7] px-3 py-3 text-sm space-y-3">
            <div className="font-medium text-ink">
              Export leads to CSV
              <span className="text-gs-text-muted font-normal"> · opens in Excel · send to the client to verify before billing</span>
            </div>
            <div className="flex flex-wrap items-end gap-3">
              <div>
                <label htmlFor="export-from" className="text-xs text-gs-text-muted block mb-1">From</label>
                <input
                  id="export-from"
                  type="date"
                  value={exportFrom}
                  onChange={(e) => setExportFrom(e.target.value)}
                  className="px-2 py-1 border border-[#D8D8D0] rounded text-sm"
                />
              </div>
              <div>
                <label htmlFor="export-to" className="text-xs text-gs-text-muted block mb-1">To</label>
                <input
                  id="export-to"
                  type="date"
                  value={exportTo}
                  onChange={(e) => setExportTo(e.target.value)}
                  className="px-2 py-1 border border-[#D8D8D0] rounded text-sm"
                />
              </div>
              <button
                type="button"
                onClick={exportLeads}
                disabled={exporting}
                className="px-4 py-2 rounded-lg text-sm font-semibold disabled:opacity-60"
                style={{ background: tenantConfig.primaryColor, color: '#ffffff' }}
              >
                {exporting ? 'Exporting…' : 'Export leads (CSV)'}
              </button>
            </div>
            {billingSummary && (
              <div className="text-sm text-ink border-t border-[#E8E8E2] pt-3">
                <span className="font-medium">{billingSummary.billable}</span> billable of{' '}
                <span className="font-medium">{billingSummary.events}</span> leads in this range ·{' '}
                <span className="font-medium">${billingSummary.total_amount_aud.toLocaleString('en-AU', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span> total
                <span className="text-gs-text-muted"> · from the billing ledger (repeats $0)</span>
              </div>
            )}
            {exportMsg && <div className="text-sm text-gs-text-muted">{exportMsg}</div>}
          </div>
        </section>
        )}
      </div>

      {showInstModal && (
        <InstitutionModal
          onClose={() => setShowInstModal(false)}
          onCreated={(inst) => setInstitutions((prev) => [...prev, inst])}
        />
      )}
      {showProdModal && (
        <ProductModal
          institutions={institutions}
          onClose={() => setShowProdModal(false)}
          onCreated={(prod) => setProducts((prev) => [prod, ...prev])}
        />
      )}
      {showPwModal && <ChangePasswordModal onClose={() => setShowPwModal(false)} />}

      {shotRows && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50" role="dialog" aria-label="Review screenshot rates">
          <div className="bg-white rounded-2xl p-6 w-full max-w-2xl max-h-[85vh] flex flex-col">
            <h3 className="font-heading text-xl text-ink mb-1">Review rates from screenshot</h3>
            <p className="text-sm text-gs-text-muted mb-4">
              Check each value before it goes live, fix any misread, then apply. {shotRows.length} products read.
            </p>
            <div className="overflow-y-auto flex-1 -mx-2 px-2">
              <table className="w-full text-sm">
                <thead className="text-xs text-gs-text-muted text-left">
                  <tr>
                    <th className="py-1 pr-2">Lender</th>
                    <th className="pr-2">Product</th>
                    <th className="pr-2 w-20">Rate %</th>
                    <th className="pr-2 w-20">Comp %</th>
                    <th className="pr-2 w-24">Type</th>
                    <th className="w-6" />
                  </tr>
                </thead>
                <tbody>
                  {shotRows.map((r, i) => (
                    <tr key={i} className="border-t border-[#EEEEE8]">
                      <td className="py-1 pr-2">
                        <input value={r.lender_name} onChange={(e) => editShotRow(i, { lender_name: e.target.value })}
                          className="w-full py-1 px-2 border border-[#E8E8E2] rounded" />
                      </td>
                      <td className="pr-2">
                        <input value={r.product_name} onChange={(e) => editShotRow(i, { product_name: e.target.value })}
                          className="w-full py-1 px-2 border border-[#E8E8E2] rounded" />
                      </td>
                      <td className="pr-2">
                        <input type="number" step={0.01} value={r.rate}
                          onChange={(e) => editShotRow(i, { rate: parseFloat(e.target.value) || 0 })}
                          className="w-full py-1 px-2 border border-[#E8E8E2] rounded tab-num" />
                      </td>
                      <td className="pr-2">
                        <input type="number" step={0.01} value={r.comparison_rate ?? ''}
                          aria-label="Comparison rate"
                          onChange={(e) => editShotRow(i, { comparison_rate: e.target.value === '' ? null : parseFloat(e.target.value) })}
                          className="w-full py-1 px-2 border border-[#E8E8E2] rounded tab-num" placeholder="comp." />
                      </td>
                      <td className="pr-2">
                        <select value={r.product_type || 'variable'} onChange={(e) => editShotRow(i, { product_type: e.target.value })}
                          className="w-full py-1 px-1 border border-[#E8E8E2] rounded">
                          <option value="variable">variable</option>
                          <option value="fixed">fixed</option>
                        </select>
                      </td>
                      <td>
                        <button type="button" aria-label="Remove row"
                          onClick={() => setShotRows((prev) => (prev ? prev.filter((_, idx) => idx !== i) : prev))}
                          className="text-gs-text-muted hover:text-red-500">✕</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="flex justify-end gap-2 mt-4 pt-3 border-t border-[#EEEEE8]">
              <button onClick={() => setShotRows(null)} disabled={shotBusy}
                className="text-sm px-4 py-2 rounded-lg border border-[#D8D8D0] hover:bg-[#FAFAF7] disabled:opacity-50">Cancel</button>
              <button onClick={applyShotRows} disabled={shotBusy || shotRows.length === 0}
                className="text-sm font-medium px-4 py-2 rounded-lg disabled:opacity-50"
                style={{ background: tenantConfig.accentColor, color: tenantConfig.primaryColor }}>
                {shotBusy ? 'Applying…' : `Confirm & apply ${shotRows.length}`}
              </button>
            </div>
          </div>
        </div>
      )}
    </main>
  )
}

// -------- Institution modal --------
// -------- Change-password modal --------
const MIN_PASSWORD_LENGTH = 8

function ChangePasswordModal({ onClose }: { onClose: () => void }) {
  const [currentPw, setCurrentPw] = useState('')
  const [newPw, setNewPw] = useState('')
  const [confirmPw, setConfirmPw] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [done, setDone] = useState(false)
  const [submitting, setSubmitting] = useState(false)

  async function submit(e: FormEvent) {
    e.preventDefault()
    setError(null)
    if (newPw.length < MIN_PASSWORD_LENGTH) {
      setError(`New password must be at least ${MIN_PASSWORD_LENGTH} characters.`)
      return
    }
    if (newPw !== confirmPw) {
      setError('New password and confirmation do not match.')
      return
    }
    if (newPw === currentPw) {
      setError('New password must be different from the current one.')
      return
    }
    setSubmitting(true)
    try {
      await api.post('/api/auth/change-password', { current_password: currentPw, new_password: newPw })
      setDone(true)
    } catch (err: unknown) {
      const status = (err as { status?: number })?.status
      setError(status === 400 ? 'Current password is incorrect.' : (err as Error).message)
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50" role="dialog" aria-label="Change password">
      <div className="bg-white rounded-2xl shadow-card p-8 max-w-md w-full">
        <h3 className="font-heading text-xl text-ink mb-4">Change password</h3>
        {done ? (
          <div className="space-y-4">
            <p className="text-sm text-ink">Your password has been changed. Use it next time you sign in.</p>
            <div className="flex justify-end">
              <button onClick={onClose} className="px-4 py-2 rounded-lg text-sm font-semibold" style={{ background: tenantConfig.accentColor, color: tenantConfig.primaryColor }}>Done</button>
            </div>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-4">
            <div>
              <label htmlFor="pw-current" className="block text-sm font-medium mb-1">Current password</label>
              <input id="pw-current" type="password" autoComplete="current-password" value={currentPw} onChange={(e) => setCurrentPw(e.target.value)} required className="w-full px-3 py-2 border border-[#D8D8D0] rounded-lg text-sm" />
            </div>
            <div>
              <label htmlFor="pw-new" className="block text-sm font-medium mb-1">New password</label>
              <input id="pw-new" type="password" autoComplete="new-password" value={newPw} onChange={(e) => setNewPw(e.target.value)} required minLength={MIN_PASSWORD_LENGTH} className="w-full px-3 py-2 border border-[#D8D8D0] rounded-lg text-sm" />
              <p className="text-xs text-gs-text-muted mt-1">At least {MIN_PASSWORD_LENGTH} characters.</p>
            </div>
            <div>
              <label htmlFor="pw-confirm" className="block text-sm font-medium mb-1">Confirm new password</label>
              <input id="pw-confirm" type="password" autoComplete="new-password" value={confirmPw} onChange={(e) => setConfirmPw(e.target.value)} required className="w-full px-3 py-2 border border-[#D8D8D0] rounded-lg text-sm" />
            </div>
            {error && <div className="text-red-600 text-sm">{error}</div>}
            <div className="flex justify-end gap-3">
              <button type="button" onClick={onClose} className="px-4 py-2 text-sm">Cancel</button>
              <button type="submit" disabled={submitting} className="px-4 py-2 rounded-lg text-sm font-semibold" style={{ background: tenantConfig.accentColor, color: tenantConfig.primaryColor }}>
                {submitting ? 'Saving…' : 'Change password'}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  )
}

function InstitutionModal({
  onClose,
  onCreated,
}: {
  onClose: () => void
  onCreated: (i: Institution) => void
}) {
  const [name, setName] = useState('')
  const [code, setCode] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [submitting, setSubmitting] = useState(false)

  async function submit(e: FormEvent) {
    e.preventDefault()
    setSubmitting(true)
    setError(null)
    try {
      const inst = await api.post<Institution>('/api/admin/institutions', { name, code })
      onCreated(inst)
      onClose()
    } catch (err: unknown) {
      const status = (err as { status?: number })?.status
      setError(status === 409 ? 'That code already exists.' : (err as Error).message)
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50" role="dialog" aria-label="New institution">
      <div className="bg-white rounded-2xl shadow-card p-8 max-w-md w-full">
        <h3 className="font-heading text-xl text-ink mb-4">New institution</h3>
        <form onSubmit={submit} className="space-y-4">
          <div>
            <label htmlFor="inst-name" className="block text-sm font-medium mb-1">Name</label>
            <input id="inst-name" value={name} onChange={(e) => setName(e.target.value)} required className="w-full px-3 py-2 border border-[#D8D8D0] rounded-lg text-sm" />
          </div>
          <div>
            <label htmlFor="inst-code" className="block text-sm font-medium mb-1">Code</label>
            <input id="inst-code" value={code} onChange={(e) => setCode(e.target.value.toUpperCase())} required maxLength={10} className="w-full px-3 py-2 border border-[#D8D8D0] rounded-lg text-sm uppercase" />
          </div>
          {error && <div className="text-red-600 text-sm">{error}</div>}
          <div className="flex justify-end gap-3">
            <button type="button" onClick={onClose} className="px-4 py-2 text-sm">Cancel</button>
            <button type="submit" disabled={submitting} className="px-4 py-2 rounded-lg text-sm font-semibold" style={{ background: tenantConfig.accentColor, color: tenantConfig.primaryColor }}>
              {submitting ? 'Saving…' : 'Create'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

// -------- Product modal --------
function ProductModal({
  institutions,
  onClose,
  onCreated,
}: {
  institutions: Institution[]
  onClose: () => void
  onCreated: (p: Product) => void
}) {
  const [instId, setInstId] = useState<string>(institutions[0]?.id ?? '')
  const [name, setName] = useState('')
  const [ptype, setPtype] = useState<'variable' | 'fixed' | 'split'>('variable')
  const [rate, setRate] = useState('')
  const [comp, setComp] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [submitting, setSubmitting] = useState(false)

  async function submit(e: FormEvent) {
    e.preventDefault()
    setSubmitting(true)
    setError(null)
    try {
      const prod = await api.post<Product>('/api/admin/loans', {
        institution_id: instId,
        product_name: name,
        product_type: ptype,
        interest_rate: parseFloat(rate),
        comparison_rate: comp.trim() === '' ? null : parseFloat(comp),
      })
      onCreated(prod)
      onClose()
    } catch (err: unknown) {
      const status = (err as { status?: number })?.status
      setError(status === 409 ? 'That product already exists for this institution.' : (err as Error).message)
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50" role="dialog" aria-label="New product">
      <div className="bg-white rounded-2xl shadow-card p-8 max-w-md w-full">
        <h3 className="font-heading text-xl text-ink mb-4">Add product</h3>
        <form onSubmit={submit} className="space-y-4">
          <div>
            <label htmlFor="prod-inst" className="block text-sm font-medium mb-1">Institution</label>
            <select id="prod-inst" value={instId} onChange={(e) => setInstId(e.target.value)} required className="w-full px-3 py-2 border border-[#D8D8D0] rounded-lg text-sm bg-white">
              {institutions.map((i) => <option key={i.id} value={i.id}>{i.name}</option>)}
            </select>
          </div>
          <div>
            <label htmlFor="prod-name" className="block text-sm font-medium mb-1">Product name</label>
            <input id="prod-name" value={name} onChange={(e) => setName(e.target.value)} required className="w-full px-3 py-2 border border-[#D8D8D0] rounded-lg text-sm" />
          </div>
          <div>
            <label htmlFor="prod-type" className="block text-sm font-medium mb-1">Type</label>
            <select id="prod-type" value={ptype} onChange={(e) => setPtype(e.target.value as 'variable' | 'fixed' | 'split')} className="w-full px-3 py-2 border border-[#D8D8D0] rounded-lg text-sm bg-white">
              <option value="variable">Variable</option>
              <option value="fixed">Fixed</option>
              <option value="split">Split</option>
            </select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label htmlFor="prod-rate" className="block text-sm font-medium mb-1">Rate (%)</label>
              <input id="prod-rate" type="number" step="0.01" value={rate} onChange={(e) => setRate(e.target.value)} required className="w-full px-3 py-2 border border-[#D8D8D0] rounded-lg text-sm tab-num" placeholder="e.g. 5.79" />
            </div>
            <div>
              <label htmlFor="prod-comp" className="block text-sm font-medium mb-1">Comparison rate (%)</label>
              <input id="prod-comp" type="number" step="0.01" value={comp} onChange={(e) => setComp(e.target.value)} className="w-full px-3 py-2 border border-[#D8D8D0] rounded-lg text-sm tab-num" placeholder="e.g. 5.83" />
            </div>
          </div>
          {error && <div className="text-red-600 text-sm">{error}</div>}
          <div className="flex justify-end gap-3">
            <button type="button" onClick={onClose} className="px-4 py-2 text-sm">Cancel</button>
            <button type="submit" disabled={submitting} className="px-4 py-2 rounded-lg text-sm font-semibold" style={{ background: tenantConfig.accentColor, color: tenantConfig.primaryColor }}>
              {submitting ? 'Saving…' : 'Add product'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

// -------- Editable institution row --------
function InstitutionRow({
  institution,
  onSave,
  onToggle,
}: {
  institution: Institution
  onSave: (id: string, patch: Partial<Institution>) => Promise<boolean>
  onToggle: (id: string) => Promise<void>
}) {
  const [editing, setEditing] = useState(false)
  const [name, setName] = useState(institution.name)
  const [code, setCode] = useState(institution.code)
  const [saving, setSaving] = useState(false)

  async function commit() {
    setSaving(true)
    const patch: Partial<Institution> = {}
    if (name !== institution.name) patch.name = name
    if (code !== institution.code) patch.code = code
    const ok = Object.keys(patch).length === 0 || (await onSave(institution.id, patch))
    setSaving(false)
    if (ok) setEditing(false)
  }
  function cancel() {
    setName(institution.name)
    setCode(institution.code)
    setEditing(false)
  }

  if (editing) {
    return (
      <li className="px-4 py-3 space-y-2">
        <input
          value={name}
          onChange={(e) => setName(e.target.value)}
          className="w-full px-2 py-1 border border-[#D8D8D0] rounded text-sm"
          aria-label="Institution name"
        />
        <input
          value={code}
          onChange={(e) => setCode(e.target.value.toUpperCase())}
          maxLength={10}
          className="w-full px-2 py-1 border border-[#D8D8D0] rounded text-xs uppercase"
          aria-label="Institution code"
        />
        <div className="flex justify-end gap-2 text-xs">
          <button onClick={cancel} className="px-3 py-1 text-gs-text-soft hover:text-ink">Cancel</button>
          <button
            onClick={commit}
            disabled={saving}
            className="px-3 py-1 rounded font-semibold"
            style={{ background: tenantConfig.accentColor, color: tenantConfig.primaryColor }}
          >
            {saving ? 'Saving…' : 'Save'}
          </button>
        </div>
      </li>
    )
  }

  return (
    <li className="px-4 py-3 group">
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="text-sm font-medium text-ink truncate">{institution.name}</div>
          <div className="text-xs text-gs-text-muted">{institution.code}</div>
        </div>
        <button
          onClick={() => setEditing(true)}
          className="text-xs text-gs-text-muted hover:text-ink shrink-0 opacity-0 group-hover:opacity-100 transition"
          aria-label="Edit institution"
        >
          Edit
        </button>
      </div>
      <div className="flex flex-wrap items-center gap-1.5 mt-2">
        <button
          onClick={() => onSave(institution.id, { is_major: !institution.is_major })}
          className={`text-xs px-2 py-0.5 rounded-full border ${
            institution.is_major
              ? 'border-ink bg-ink text-white'
              : 'border-[#D8D8D0] text-gs-text-muted hover:border-ink hover:text-ink'
          }`}
          aria-label="Toggle major lender"
          title="Show this lender in the site's Major lenders box"
        >
          {institution.is_major ? '★ Major' : 'Major'}
        </button>
        <button
          onClick={() => onSave(institution.id, { tier: institution.tier === 1 ? 2 : institution.tier === 2 ? null : 1 })}
          className={`text-xs px-2 py-0.5 rounded-full border ${
            institution.tier
              ? 'border-ink bg-ink text-white'
              : 'border-[#D8D8D0] text-gs-text-muted hover:border-ink hover:text-ink'
          }`}
          aria-label="Cycle lender tier"
          title="Public tier. Only Tier 1 & 2 show on the lender wall. Click to cycle 1 → 2 → none."
        >
          {institution.tier ? `Tier ${institution.tier}` : 'No tier'}
        </button>
        <button
          onClick={() => onSave(institution.id, { show_in_carousel: !institution.show_in_carousel })}
          className={`text-xs px-2 py-0.5 rounded-full border ${
            institution.show_in_carousel
              ? 'border-ink bg-ink text-white'
              : 'border-[#D8D8D0] text-gs-text-muted hover:border-ink hover:text-ink'
          }`}
          aria-label="Toggle rate carousel"
          title="Include this lender's rate in the homepage rate carousel (carousel is hidden when none are selected)"
        >
          {institution.show_in_carousel ? '◉ Carousel' : 'Carousel'}
        </button>
        <button
          onClick={() => onToggle(institution.id)}
          className={`text-xs px-2 py-0.5 rounded-full ${
            institution.is_active ? 'bg-lime-tint text-ink' : 'bg-gs-muted text-gs-text-muted'
          }`}
          aria-label="Toggle institution status"
          title="Click to toggle active/inactive"
        >
          {institution.is_active ? 'Active' : 'Inactive'}
        </button>
      </div>
    </li>
  )
}

// -------- Editable product row --------
function ProductRow({
  product,
  institutions,
  institutionName,
  onSave,
  onToggle,
}: {
  product: Product
  institutions: Institution[]
  institutionName: (id: string) => string
  onSave: (
    id: string,
    patch: Partial<Product> & { interest_rate_percent?: number; comparison_rate_percent?: number | null },
  ) => Promise<boolean>
  onToggle: (id: string) => Promise<void>
}) {
  const compStr = (p: Product) => (p.comparison_rate != null ? (p.comparison_rate * 100).toFixed(2) : '')
  const [editing, setEditing] = useState(false)
  const [name, setName] = useState(product.product_name)
  const [instId, setInstId] = useState(product.institution_id)
  const [ptype, setPtype] = useState(product.product_type)
  const [ratePercent, setRatePercent] = useState((product.interest_rate * 100).toFixed(2))
  const [compPercent, setCompPercent] = useState(compStr(product))
  const [saving, setSaving] = useState(false)

  // Re-sync inputs if parent state updates (e.g. server-side changes)
  useEffect(() => {
    if (!editing) {
      setName(product.product_name)
      setInstId(product.institution_id)
      setPtype(product.product_type)
      setRatePercent((product.interest_rate * 100).toFixed(2))
      setCompPercent(compStr(product))
    }
  }, [product, editing])

  async function commit() {
    setSaving(true)
    const patch: Partial<Product> & { interest_rate_percent?: number; comparison_rate_percent?: number | null } = {}
    if (name !== product.product_name) patch.product_name = name
    if (instId !== product.institution_id) patch.institution_id = instId
    if (ptype !== product.product_type) patch.product_type = ptype
    const enteredRate = parseFloat(ratePercent)
    if (Number.isFinite(enteredRate) && Math.abs(enteredRate - product.interest_rate * 100) > 0.001) {
      patch.interest_rate_percent = enteredRate
    }
    // Comparison rate: send the new value when it changed (blank clears it to null).
    const enteredComp = compPercent.trim() === '' ? null : parseFloat(compPercent)
    if (enteredComp !== (product.comparison_rate != null ? Number((product.comparison_rate * 100).toFixed(2)) : null)) {
      patch.comparison_rate_percent = enteredComp
    }
    const ok = Object.keys(patch).length === 0 || (await onSave(product.id, patch))
    setSaving(false)
    if (ok) setEditing(false)
  }
  function cancel() {
    setName(product.product_name)
    setInstId(product.institution_id)
    setPtype(product.product_type)
    setRatePercent((product.interest_rate * 100).toFixed(2))
    setCompPercent(compStr(product))
    setEditing(false)
  }

  const inputCls = 'w-full px-2 py-1 border border-[#D8D8D0] rounded text-sm bg-white'

  if (editing) {
    return (
      <tr className="bg-lime-tint/40">
        <td className="px-4 py-3">
          <input value={name} onChange={(e) => setName(e.target.value)} className={inputCls} aria-label="Product name" />
        </td>
        <td className="px-4 py-3">
          <select value={instId} onChange={(e) => setInstId(e.target.value)} className={inputCls} aria-label="Institution">
            {institutions.map((i) => (
              <option key={i.id} value={i.id}>{i.name}</option>
            ))}
          </select>
        </td>
        <td className="px-4 py-3">
          <select value={ptype} onChange={(e) => setPtype(e.target.value)} className={inputCls} aria-label="Product type">
            <option value="variable">Variable</option>
            <option value="fixed">Fixed</option>
            <option value="split">Split</option>
            <option value="construction">Construction</option>
            <option value="bridging">Bridging</option>
          </select>
        </td>
        <td className="px-4 py-3">
          <div className="flex items-center gap-1 justify-end">
            <input
              type="number"
              step="0.01"
              value={ratePercent}
              onChange={(e) => setRatePercent(e.target.value)}
              className={`${inputCls} text-right tab-num w-20`}
              aria-label="Interest rate percent"
            />
            <span className="text-xs text-gs-text-muted">%</span>
          </div>
          <div className="flex items-center gap-1 justify-end mt-1">
            <input
              type="number"
              step="0.01"
              value={compPercent}
              onChange={(e) => setCompPercent(e.target.value)}
              className={`${inputCls} text-right tab-num w-20 text-[11px]`}
              aria-label="Comparison rate percent"
              placeholder="comp."
            />
            <span className="text-[10px] text-gs-text-muted">% comp.</span>
          </div>
        </td>
        <td className="px-4 py-3 text-center text-xs text-gs-text-muted">
          {product.is_active ? 'Active' : 'Inactive'}
        </td>
        <td className="px-4 py-3 text-right">
          <div className="flex justify-end gap-1">
            <button
              onClick={cancel}
              className="px-2 py-1 text-xs text-gs-text-soft hover:text-ink"
            >
              Cancel
            </button>
            <button
              onClick={commit}
              disabled={saving}
              className="px-2 py-1 rounded text-xs font-semibold"
              style={{ background: tenantConfig.accentColor, color: tenantConfig.primaryColor }}
            >
              {saving ? '…' : 'Save'}
            </button>
          </div>
        </td>
      </tr>
    )
  }

  return (
    <tr className="group hover:bg-[#FAFAF7]">
      <td className="px-4 py-3 font-medium text-ink">{product.product_name}</td>
      <td className="px-4 py-3 text-gs-text-soft">{institutionName(product.institution_id)}</td>
      <td className="px-4 py-3 text-gs-text-soft capitalize">{product.product_type}</td>
      <td className="px-4 py-3 text-right tab-num text-ink font-medium">
        {(product.interest_rate * 100).toFixed(2)}%
        {product.comparison_rate != null && (
          <div className="text-[10px] text-gs-text-muted font-normal">
            {(product.comparison_rate * 100).toFixed(2)}% comp.
          </div>
        )}
        <button
          type="button"
          onClick={() => onSave(product.id, { rate_locked: !product.rate_locked })}
          title={
            product.rate_locked
              ? 'Locked to manual. The rate scrape will NOT overwrite this. Click to unlock.'
              : 'Auto. The rate scrape can update this. Click to lock to manual.'
          }
          aria-label={product.rate_locked ? 'Unlock rate (currently locked to manual)' : 'Lock rate to manual'}
          className={`mt-1 inline-flex items-center gap-1 text-[10px] font-semibold px-1.5 py-0.5 rounded-full border ${
            product.rate_locked
              ? 'bg-ink text-white border-ink'
              : 'bg-white text-gs-text-muted border-[#D8D8D0] hover:text-ink'
          }`}
        >
          {product.rate_locked ? '🔒 Manual' : '🔓 Auto'}
        </button>
      </td>
      <td className="px-4 py-3 text-center">
        <button
          role="switch"
          aria-checked={product.is_active}
          aria-label="Toggle status"
          onClick={() => onToggle(product.id)}
          className={`relative inline-flex h-5 w-10 rounded-full transition ${
            product.is_active ? 'bg-ink' : 'bg-[#D8D8D0]'
          }`}
        >
          <span
            className={`inline-block h-4 w-4 transform rounded-full bg-white transition ${
              product.is_active ? 'translate-x-5' : 'translate-x-1'
            } mt-0.5`}
          />
        </button>
      </td>
      <td className="px-4 py-3 text-right">
        <button
          onClick={() => setEditing(true)}
          className="text-xs text-gs-text-muted hover:text-ink opacity-0 group-hover:opacity-100 transition"
        >
          Edit
        </button>
      </td>
    </tr>
  )
}
